import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, CandlestickChart } from "lucide-react";

import type { WatchlistSortState } from "@/components/grid/WatchlistGrid";
import { GlobalAppMenu } from "@/components/nav/GlobalAppMenu";
import { SymbolSearchInput } from "@/components/symbol/SymbolSearchInput";
import { AddIntoWatchlistModal } from "@/components/watchlist/AddIntoWatchlistModal";
import { WatchlistWorkspace } from "@/components/watchlist/WatchlistWorkspace";
import { PointFigureCanvas } from "@/components/wyckoff/PointFigureCanvas";
import { WyckoffChart } from "@/components/wyckoff/WyckoffChart";
import { useBarsQuery, useBootstrapQuery } from "@/features/market-data/queries";
import { analyzeWyckoffLocal, type LocalWyckoffAnalysis, type WyckoffMarker, type WyckoffRow } from "@/features/wyckoff/local-analysis";
import {
  buildPointFigureColumns,
  calcPnfTarget,
  clampPnfReversal,
  computeAtrBoxSize,
  computePercentBoxSize,
  type PnfBoxMode,
  type PnfMethod,
  type PnfTargetMode,
} from "@/features/wyckoff/pnf";
import { useWatchlistItemsSortedQuery, useWatchlistsQuery } from "@/features/watchlist/queries";
import { normalizeTerminalSymbol } from "@/lib/symbol";
import { formatCompact, formatNumber } from "@/lib/utils";
import { apiClient } from "@/services/api/client";
import type { Watchlist, WatchlistItem, WyckoffAnalysis } from "@/services/api/types";
import { useUiStore } from "@/stores/ui-store";

const WYCKOFF_SESSION_STORAGE_KEY = "lisaq:wyckoff-session";
const INTERVAL_OPTIONS = ["D", "W", "M"] as const;
const ADJUST_OPTIONS = ["qfq", "none", "hfq"] as const;

interface WyckoffSessionState {
  interval: (typeof INTERVAL_OPTIONS)[number];
  adjust: (typeof ADJUST_OPTIONS)[number];
  watchlistView: "single" | "compact";
  chartSubPane: "vol" | "atr" | "rsi";
  chartMainPaneRatio: number;
  limit: number;
  pnfBoxMode: PnfBoxMode;
  pnfBoxSize: number;
  pnfPercent: number;
  pnfReversal: number;
  pnfMethod: PnfMethod;
}

interface LiveQuoteSnapshot {
  close: number | null;
  pct_chg: number | null;
  pre_close: number | null;
  change: number | null;
  trade_date: string | null;
  vol: number | null;
  amount: number | null;
  source: string | null;
}

export function WyckoffTerminalPage() {
  const search = useSearch({ from: "/wyckoff" });
  const navigate = useNavigate({ from: "/wyckoff" });
  const queryClient = useQueryClient();
  const activeBarsDatasetRef = useRef<string | null>(null);
  const saveTimerRef = useRef<number | null>(null);
  const sessionHydratedRef = useRef(false);
  const profileSettingsRef = useRef<Record<string, unknown>>({});
  const [searchInput, setSearchInput] = useState(search.symbol);
  const [watchlistView, setWatchlistView] = useState<"single" | "compact">("single");
  const [chartSubPane, setChartSubPane] = useState<"vol" | "atr" | "rsi">("vol");
  const [chartMainPaneRatio, setChartMainPaneRatio] = useState(0.76);
  const [addIntoWatchlistOpen, setAddIntoWatchlistOpen] = useState(false);
  const [pnfBoxMode, setPnfBoxMode] = useState<PnfBoxMode>("atr");
  const [pnfBoxSize, setPnfBoxSize] = useState(0);
  const [pnfPercent, setPnfPercent] = useState(1);
  const [pnfReversal, setPnfReversal] = useState(3);
  const [pnfMethod, setPnfMethod] = useState<PnfMethod>("hilo");
  const [chartVisibleRange, setChartVisibleRange] = useState<{ from: string; to: string } | null>(null);
  const [selectedEventTime, setSelectedEventTime] = useState<string | null>(null);
  const [hoverCandle, setHoverCandle] = useState<{ open: number; high: number; low: number; close: number } | null>(null);
  const [symbolLiveQuote, setSymbolLiveQuote] = useState<LiveQuoteSnapshot | null>(null);

  const bootstrapQuery = useBootstrapQuery();
  const watchlistsQuery = useWatchlistsQuery();
  const barsQuery = useBarsQuery(search.symbol, search.interval, search.adjust, search.limit);

  const watchlistsData = watchlistsQuery.data || bootstrapQuery.data?.watchlists;
  const watchlistMeta = watchlistsData?.watchlists || [];
  const maxListsPerUser = watchlistsData?.max_lists_per_user ?? 100;
  const selectedWatchlistId = search.watchlistId ?? watchlistsData?.default_watchlist_id ?? watchlistMeta[0]?.id ?? null;
  const currentWatchlist =
    watchlistMeta.find((item) => Number(item.id) === Number(selectedWatchlistId)) ||
    watchlistMeta[0] ||
    null;

  const currentSortState: WatchlistSortState = useMemo(
    () => ({
      field: normalizeWatchlistSortField(currentWatchlist?.sort_by || "pct_chg"),
      direction: currentWatchlist?.sort_dir === "asc" ? "asc" : "desc",
    }),
    [currentWatchlist?.sort_by, currentWatchlist?.sort_dir],
  );
  const watchlistItemsQuery = useWatchlistItemsSortedQuery(selectedWatchlistId, {
    sortBy: currentSortState.field,
    sortDir: currentSortState.direction,
    limit: 5000,
  });
  const watchRows = watchlistItemsQuery.data?.items || [];
  const watchlistPanelWidth = useMemo(() => {
    if (watchlistView !== "single") {
      return 280;
    }
    return measureSingleWatchlistWidth(watchRows);
  }, [watchRows, watchlistView]);
  const workspaceGridStyle = useMemo(
    () =>
      ({
        "--wyckoff-grid-cols": `${watchlistPanelWidth}px minmax(0,1fr) minmax(0,1fr)`,
      }) as CSSProperties,
    [watchlistPanelWidth],
  );

  const currentUser = bootstrapQuery.data?.current_user;
  const displayUserName = currentUser?.display_name || currentUser?.email || "Guest";
  const activeWatchItem = watchRows.find((item) => item.symbol === search.symbol) || null;
  const displayName = activeWatchItem?.display_name || activeWatchItem?.stock_name || activeWatchItem?.name_cache || search.symbol;
  const percentChange = symbolLiveQuote?.pct_chg ?? activeWatchItem?.pct_chg ?? null;
  const latestPriceValue = symbolLiveQuote?.close ?? activeWatchItem?.close ?? null;
  const tradeDateValue = symbolLiveQuote?.trade_date ?? activeWatchItem?.trade_date ?? null;
  const isUp = (percentChange ?? 0) >= 0;
  const barsDatasetKey = `${search.symbol}:${search.interval}:${search.adjust}:${search.limit}`;
  const [chartBars, setChartBars] = useState(() => normalizeBarsPayload([]));
  const barsFingerprint = useMemo(() => {
    const first = chartBars[0]?.time || "";
    const last = chartBars[chartBars.length - 1]?.time || "";
    const tail = chartBars[chartBars.length - 1];
    const tailSignature = tail ? `${tail.time}:${tail.open}:${tail.high}:${tail.low}:${tail.close}:${tail.volume}` : "";
    return `${first}:${last}:${chartBars.length}:${tailSignature}`;
  }, [chartBars]);
  const shouldUseLocalLiveAnalysis = search.interval === "D" && symbolLiveQuote?.source === "tvscreener";

  const { setMarketConnection, setApiLatencyMs, setLastMarketUpdate } = useUiStore();

  useEffect(() => {
    const onLatency = (event: Event) => {
      const detail = (event as CustomEvent<number>).detail;
      setApiLatencyMs(detail);
    };
    window.addEventListener("lisaq:api-latency", onLatency as EventListener);
    return () => window.removeEventListener("lisaq:api-latency", onLatency as EventListener);
  }, [setApiLatencyMs]);

  useEffect(() => {
    setSearchInput(search.symbol);
  }, [search.symbol]);

  useEffect(() => {
    setSelectedEventTime(null);
    setChartVisibleRange(null);
    setHoverCandle(null);
  }, [search.symbol, search.interval, search.adjust]);

  useEffect(() => {
    if (!barsQuery.data) {
      return;
    }
    const incomingBars = normalizeBarsPayload(barsQuery.data.bars || []);
    setChartBars((prev) => {
      if (activeBarsDatasetRef.current !== barsDatasetKey) {
        activeBarsDatasetRef.current = barsDatasetKey;
        return incomingBars;
      }
      return mergeBars(prev, incomingBars);
    });
  }, [barsDatasetKey, barsQuery.data]);

  useEffect(() => {
    let disposed = false;
    let inflight = false;
    setSymbolLiveQuote(null);

    const tick = async (force = false) => {
      if (disposed || inflight || (!force && document.hidden)) {
        return;
      }
      inflight = true;
      try {
        const payload = await apiClient.getTodayBar(search.symbol, search.adjust, { syncLive: true });
        const normalizedLive = normalizeLiveQuote(payload?.bar, payload?.trade_date);
        if (normalizedLive) {
          setSymbolLiveQuote(normalizedLive);
        }
        const incoming = payload?.bar ? normalizeBarsPayload([payload.bar]) : [];
        if (incoming.length && search.interval === "D") {
          setChartBars((prev) => mergeBars(prev, incoming));
        }
        if (payload.source === "tvscreener") {
          setLastMarketUpdate(new Date().toLocaleTimeString("zh-CN"));
        }
      } catch {
        return;
      } finally {
        inflight = false;
      }
    };

    void tick(true);
    const timer = window.setInterval(() => {
      void tick();
    }, 5000);
    const onVisibilityChange = () => {
      if (!document.hidden) {
        void tick(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [search.symbol, search.interval, search.adjust, setLastMarketUpdate]);

  useEffect(() => {
    if (!watchlistMeta.length) {
      return;
    }
    const valid = selectedWatchlistId != null && watchlistMeta.some((item) => Number(item.id) === Number(selectedWatchlistId));
    if (valid) {
      return;
    }
    patchSearch({ watchlistId: Number(watchlistMeta[0]?.id) || null });
  }, [selectedWatchlistId, watchlistMeta]);

  useEffect(() => {
    setMarketConnection("connected");
    setLastMarketUpdate(new Date().toLocaleTimeString("zh-CN"));
  }, [search.adjust, search.interval, search.symbol, setLastMarketUpdate, setMarketConnection]);

  useEffect(() => {
    const backendSettings = (bootstrapQuery.data?.watchlists.profile?.settings || {}) as Record<string, unknown>;
    profileSettingsRef.current = backendSettings;
    if (sessionHydratedRef.current || !bootstrapQuery.data) {
      return;
    }
    const localSession = readWyckoffSessionFromLocal();
    const backendSession = parseWyckoffSession(backendSettings.wyckoff_session);
    const session = localSession || backendSession;
    if (session) {
      setWatchlistView(session.watchlistView);
      setChartSubPane(session.chartSubPane);
      setChartMainPaneRatio(session.chartMainPaneRatio);
      setPnfBoxMode(session.pnfBoxMode);
      setPnfBoxSize(session.pnfBoxSize);
      setPnfPercent(session.pnfPercent);
      setPnfReversal(session.pnfReversal);
      setPnfMethod(session.pnfMethod);
      navigate({
        search: (prev) => ({
          ...prev,
          interval: session.interval,
          adjust: session.adjust,
          limit: session.limit,
        }),
        replace: true,
      });
    }
    sessionHydratedRef.current = true;
  }, [bootstrapQuery.data, navigate]);

  useEffect(() => {
    if (!sessionHydratedRef.current) {
      return;
    }
    const session: WyckoffSessionState = {
      interval: search.interval,
      adjust: search.adjust,
      watchlistView,
      chartSubPane,
      chartMainPaneRatio,
      limit: search.limit,
      pnfBoxMode,
      pnfBoxSize,
      pnfPercent,
      pnfReversal,
      pnfMethod,
    };
    writeWyckoffSessionToLocal(session);
    if (!currentUser) {
      return;
    }
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      const mergedSettings = { ...profileSettingsRef.current, wyckoff_session: session };
      void apiClient.patchProfileMe({ settings: mergedSettings }).then(() => {
        profileSettingsRef.current = mergedSettings;
      });
    }, 500);
  }, [
    chartMainPaneRatio,
    chartSubPane,
    currentUser,
    pnfBoxMode,
    pnfBoxSize,
    pnfMethod,
    pnfPercent,
    pnfReversal,
    search.adjust,
    search.interval,
    search.limit,
    watchlistView,
  ]);

  useEffect(() => {
    if (saveTimerRef.current !== null) {
      return () => window.clearTimeout(saveTimerRef.current as number);
    }
    return;
  }, []);

  const localAnalysis = useMemo(() => analyzeWyckoffLocal(chartBars), [chartBars]);
  const wyckoffQuery = useQuery({
    queryKey: ["wyckoff", "analysis", search.symbol, search.interval, search.adjust, barsFingerprint],
    queryFn: () =>
      apiClient.analyzeWyckoff({
        bars: chartBars.map((bar) => ({
          time: bar.time,
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
          volume: bar.volume,
        })),
      }),
    enabled: chartBars.length >= 120 && !shouldUseLocalLiveAnalysis,
    staleTime: 45_000,
  });

  const wyckoff = useMemo(() => {
    if (!shouldUseLocalLiveAnalysis && wyckoffQuery.data) {
      return normalizeWyckoffPayload(wyckoffQuery.data, chartBars);
    }
    return localAnalysis;
  }, [chartBars, localAnalysis, shouldUseLocalLiveAnalysis, wyckoffQuery.data]);

  const summary = wyckoff.summary;
  const latestEvent = wyckoff.events[0] || null;
  const latestBar = chartBars[chartBars.length - 1];
  const shownCandle = hoverCandle || latestBar || null;
  const trHighSeries = useMemo(
    () =>
      wyckoff.rows
        .filter((row) => Number.isFinite(row.tr_high))
        .map((row) => ({
          time: row.time,
          value: Number(row.tr_high),
        })),
    [wyckoff.rows],
  );
  const trLowSeries = useMemo(
    () =>
      wyckoff.rows
        .filter((row) => Number.isFinite(row.tr_low))
        .map((row) => ({
          time: row.time,
          value: Number(row.tr_low),
        })),
    [wyckoff.rows],
  );
  const latestAtr = useMemo(() => {
    for (let i = wyckoff.rows.length - 1; i >= 0; i -= 1) {
      const atr = wyckoff.rows[i].atr;
      if (Number.isFinite(atr)) {
        return Number(atr);
      }
    }
    return null;
  }, [wyckoff.rows]);

  const pnfBars = useMemo(() => {
    if (!chartBars.length) {
      return [];
    }
    if (!chartVisibleRange?.from || !chartVisibleRange?.to) {
      return chartBars.slice();
    }
    const from = toTradeDateDigits(chartVisibleRange.from);
    const to = toTradeDateDigits(chartVisibleRange.to);
    if (!from || !to) {
      return chartBars.slice();
    }
    const min = from <= to ? from : to;
    const max = from <= to ? to : from;
    const selected = chartBars.filter((bar) => {
      const tradeDate = toTradeDateDigits(bar.time);
      return tradeDate >= min && tradeDate <= max;
    });
    if (selected.length >= 20) {
      return selected;
    }
    return chartBars.slice(-Math.min(120, chartBars.length));
  }, [chartBars, chartVisibleRange]);
  const atrBoxSize = useMemo(() => {
    if (!chartBars.length) {
      return 0.5;
    }
    return computeAtrBoxSize({
      lastClose: chartBars[chartBars.length - 1].close,
      latestAtr,
    });
  }, [chartBars, latestAtr]);
  const percentBoxSize = useMemo(() => {
    if (!chartBars.length) {
      return 0.5;
    }
    return computePercentBoxSize({
      lastClose: chartBars[chartBars.length - 1].close,
      percent: pnfPercent,
    });
  }, [chartBars, pnfPercent]);
  const manualBoxSize = Math.max(0.01, Number(pnfBoxSize) || atrBoxSize || 0.5);
  const effectivePnfBoxSize =
    pnfBoxMode === "manual" ? manualBoxSize : pnfBoxMode === "percent" ? percentBoxSize : atrBoxSize;
  const effectiveReversal = clampPnfReversal(pnfReversal);
  const pnfTargetMode: PnfTargetMode = "neutral";
  const pnfColumns = useMemo(
    () => buildPointFigureColumns(pnfBars, effectivePnfBoxSize, effectiveReversal, pnfMethod),
    [effectivePnfBoxSize, effectiveReversal, pnfBars, pnfMethod],
  );
  const pnfTarget = useMemo(
    () => calcPnfTarget(pnfColumns, effectivePnfBoxSize, effectiveReversal, pnfTargetMode),
    [effectivePnfBoxSize, effectiveReversal, pnfColumns, pnfTargetMode],
  );
  const latestSegment = wyckoff.segments[wyckoff.segments.length - 1] || null;
  const currentPhaseRangeText = latestSegment
    ? `${formatNumber(latestSegment.bottom)} ~ ${formatNumber(latestSegment.top)}`
    : trHighSeries.length && trLowSeries.length
      ? `${formatNumber(trLowSeries[trLowSeries.length - 1]?.value)} ~ ${formatNumber(trHighSeries[trHighSeries.length - 1]?.value)}`
      : "--";

  function patchSearch(next: Partial<typeof search>) {
    if (!sessionHydratedRef.current) {
      sessionHydratedRef.current = true;
    }
    navigate({
      search: (prev) => ({ ...prev, ...next }),
      replace: true,
    });
  }

  async function refreshWatchlistQueries() {
    await queryClient.invalidateQueries({ queryKey: ["watchlists"] });
  }

  async function handleCreateWatchlist(name: string) {
    const created = await apiClient.createWatchlist({ name, sort_by: "pct_chg", sort_dir: "desc" });
    await refreshWatchlistQueries();
    patchSearch({ watchlistId: created.id });
  }

  async function handleRenameWatchlist(watchlistId: number, name: string) {
    await apiClient.updateWatchlist(watchlistId, { name });
    await refreshWatchlistQueries();
  }

  async function handleCopyWatchlist(sourceWatchlistId: number, name: string) {
    const source = await apiClient.listWatchlistItems(sourceWatchlistId, { limit: 5000 });
    const created = await apiClient.createWatchlist({
      name,
      sort_by: currentWatchlist?.sort_by || "pct_chg",
      sort_dir: currentWatchlist?.sort_dir === "asc" ? "asc" : "desc",
    });
    if (source.items.length) {
      await apiClient.bulkAddWatchlistItems(
        created.id,
        source.items.map((item) => ({
          symbol: item.symbol,
          instrument_type: item.instrument_type,
          market: item.market ?? undefined,
          alias: item.alias ?? undefined,
          note: item.note ?? undefined,
          name_cache: item.name_cache ?? item.display_name ?? item.stock_name ?? undefined,
          sort_order: item.sort_order,
          pinned: Boolean(item.pinned),
          row_color: item.row_color ?? undefined,
        })),
      );
    }
    await refreshWatchlistQueries();
    patchSearch({ watchlistId: created.id });
  }

  async function handleDeleteWatchlist(watchlistId: number) {
    const deleted = await apiClient.deleteWatchlist(watchlistId);
    await refreshWatchlistQueries();
    const fallbackId = deleted.fallback_watchlist_id ?? watchlistsData?.default_watchlist_id ?? watchlistMeta[0]?.id ?? null;
    patchSearch({ watchlistId: fallbackId });
  }

  async function handleAddItemToWatchlist(symbol: string) {
    if (!selectedWatchlistId) {
      throw new Error("未选择自选股");
    }
    await apiClient.addWatchlistItem(selectedWatchlistId, { symbol });
    await refreshWatchlistQueries();
  }

  async function handleRemoveWatchlistItem(itemId: number) {
    if (!selectedWatchlistId) {
      throw new Error("未选择自选股");
    }
    await apiClient.removeWatchlistItem(selectedWatchlistId, itemId);
    await refreshWatchlistQueries();
  }

  async function handlePatchWatchlistItem(itemId: number, payload: Partial<{ pinned: boolean; row_color: string }>) {
    if (!selectedWatchlistId) {
      throw new Error("未选择自选股");
    }
    await apiClient.patchWatchlistItem(selectedWatchlistId, itemId, payload);
    await refreshWatchlistQueries();
  }

  async function handlePersistSort(nextSort: WatchlistSortState) {
    if (!selectedWatchlistId) {
      return;
    }
    const normalizedField = normalizeWatchlistSortField(nextSort.field);
    if (normalizedField === currentSortState.field && nextSort.direction === currentSortState.direction) {
      return;
    }
    await apiClient.updateWatchlist(selectedWatchlistId, {
      sort_by: normalizedField,
      sort_dir: nextSort.direction,
    });
    await refreshWatchlistQueries();
  }

  return (
    <div className="flex h-dvh min-h-[760px] flex-col overflow-auto bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] bg-[#101318]">
        <div className="flex h-12 items-center gap-3 px-3">
          <GlobalAppMenu className="shrink-0" />
          <SymbolSearchInput
            value={searchInput}
            onValueChange={setSearchInput}
            onSubmitSymbol={async (symbol) => {
              const normalized = normalizeTerminalSymbol(symbol);
              if (!normalized || normalized === search.symbol) {
                return;
              }
              setHoverCandle(null);
              patchSearch({ symbol: normalized });
            }}
            className="min-w-[180px] max-w-[240px] flex-1"
            inputClassName="h-8 rounded-md border-[var(--border)] bg-[#0c0f14] pl-9 text-sm"
            dropdownClassName="z-50"
            placeholder="代码/名称"
            maxResults={8}
          />
          <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] p-1">
            {INTERVAL_OPTIONS.map((option) => (
              <button
                key={option}
                className={`rounded px-1.5 py-1 text-[11px] ${search.interval === option ? "bg-[#2a2f37] text-white" : "text-[var(--muted)] hover:bg-white/5"}`}
                onClick={() => patchSearch({ interval: option })}
              >
                {option}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[#0c0f14] p-1">
            {ADJUST_OPTIONS.map((option) => (
              <button
                key={option}
                className={`rounded px-1.5 py-1 text-[11px] ${search.adjust === option ? "bg-[#2a2f37] text-white" : "text-[var(--muted)] hover:bg-white/5"}`}
                onClick={() => patchSearch({ adjust: option })}
              >
                {formatAdjustLabel(option)}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/profile" className="flex items-center gap-2 rounded-md border border-[var(--border)] bg-[#0c0f14] px-2 py-1.5 hover:bg-white/5">
              <UserAvatar avatarUrl={currentUser?.avatar_url} displayName={displayUserName} />
              <span className="hidden max-w-[96px] truncate text-sm text-slate-100 xl:block">{displayUserName}</span>
            </Link>
          </div>
        </div>
      </header>

      <div
        className="grid min-h-0 flex-1 [grid-template-columns:var(--wyckoff-grid-cols)] max-[1180px]:grid-cols-[minmax(220px,40vw)_minmax(0,1fr)] max-[1180px]:grid-rows-[minmax(0,1fr)_minmax(0,1fr)]"
        style={workspaceGridStyle}
      >
        <aside className="min-h-0 border-r border-[var(--border)] bg-[#101318] max-[1180px]:row-span-2">
          <WatchlistWorkspace
            watchlists={watchlistMeta}
            selectedWatchlistId={selectedWatchlistId}
            items={watchRows}
            activeSymbol={search.symbol}
            viewMode={watchlistView}
            viewOptions={["single", "compact"]}
            maxListsPerUser={maxListsPerUser}
            loading={watchlistItemsQuery.isLoading || watchlistsQuery.isLoading}
            sortState={currentSortState}
            onSelectWatchlist={(watchlistId) => patchSearch({ watchlistId })}
            onSelectSymbol={(symbol) => patchSearch({ symbol })}
            onViewModeChange={(view) => {
              if (view === "single" || view === "compact") {
                setWatchlistView(view);
              }
            }}
            onSortChange={(sort) => void handlePersistSort(sort)}
            onCreateWatchlist={(name) => handleCreateWatchlist(name)}
            onCopyWatchlist={(watchlistId, name) => handleCopyWatchlist(watchlistId, name)}
            onRenameWatchlist={(watchlistId, name) => handleRenameWatchlist(watchlistId, name)}
            onDeleteWatchlist={(watchlistId) => handleDeleteWatchlist(watchlistId)}
            onAddSymbol={(symbol) => handleAddItemToWatchlist(symbol)}
            onPinToggle={(item, pinned) => handlePatchWatchlistItem(item.id, { pinned })}
            onSetRowColor={(item, color) => handlePatchWatchlistItem(item.id, { row_color: color })}
            onRemoveItem={(item) => handleRemoveWatchlistItem(item.id)}
          />
        </aside>

        <main className="flex min-h-0 min-w-0 flex-col border-r border-[var(--border)] bg-[#0f1115] max-[1180px]:border-b max-[1180px]:border-r-0">
          <div className="flex h-14 items-center justify-between border-b border-[var(--border)] px-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold text-slate-100">{displayName}</span>
                <span className="rounded bg-[#1b2028] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">{search.symbol}</span>
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] ${
                    summary.phase === "accumulation"
                      ? "bg-emerald-500/20 text-emerald-300"
                      : summary.phase === "distribution"
                        ? "bg-rose-500/20 text-rose-300"
                        : "bg-slate-500/20 text-slate-300"
                  }`}
                >
                  {summary.phase_name}
                </span>
              </div>
              <div className={`mt-1 flex items-center gap-2 text-xs ${isUp ? "text-[var(--up)]" : "text-[var(--down)]"}`}>
                <span className="text-[22px] font-semibold leading-none">{formatNumber(latestPriceValue ?? latestBar?.close)}</span>
                <span>{percentChange != null ? `${percentChange >= 0 ? "+" : ""}${formatNumber(percentChange)}%` : "--"}</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-[var(--muted)]">
              <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[#0d1219] p-1 text-[10px]">
                {(["vol", "atr", "rsi"] as const).map((pane) => (
                  <button
                    key={pane}
                    className={`rounded px-2 py-0.5 uppercase tracking-wide ${
                      chartSubPane === pane ? "bg-[#263140] text-slate-100" : "text-[var(--muted)] hover:bg-white/5"
                    }`}
                    onClick={() => setChartSubPane(pane)}
                  >
                    {pane}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <span>开 {formatNumber(shownCandle?.open)}</span>
                <span>高 {formatNumber(shownCandle?.high)}</span>
                <span>低 {formatNumber(shownCandle?.low)}</span>
                <span>收 {formatNumber(shownCandle?.close)}</span>
              </div>
              <Link
                to="/stock"
                search={{
                  symbol: search.symbol,
                  interval: search.interval as "1" | "5" | "15" | "30" | "60" | "D" | "W" | "M",
                  adjust: search.adjust as "none" | "qfq" | "hfq",
                  indicators: ["ma"],
                  watchlistId: search.watchlistId,
                  leftPanel: search.leftPanel,
                  rightPanel: search.rightPanel,
                  layoutPreset: "terminal",
                }}
                target="_blank"
                className="inline-flex h-8 items-center gap-1 rounded border border-[var(--border)] bg-[#111924] px-2 text-xs text-slate-200 hover:bg-white/5"
              >
                <CandlestickChart className="size-3.5" />
                K 线
              </Link>
              <button
                className="inline-flex h-8 items-center gap-1 rounded border border-[var(--border)] bg-[#111924] px-2 text-xs text-slate-200 hover:bg-white/5"
                onClick={() => setAddIntoWatchlistOpen(true)}
              >
                <Plus className="size-3.5" />
                加入自选
              </button>
            </div>
          </div>
          <div className="min-h-0 flex-1">
            <WyckoffChart
              bars={chartBars}
              datasetKey={barsDatasetKey}
              trHighSeries={trHighSeries}
              trLowSeries={trLowSeries}
              markers={wyckoff.markers}
              segments={wyckoff.segments}
              activeEventTime={selectedEventTime}
              subPane={chartSubPane}
              mainPaneRatio={chartMainPaneRatio}
              onMainPaneRatioChange={setChartMainPaneRatio}
              onMarkerSelect={(time) => {
                if (time) {
                  setSelectedEventTime(time);
                }
              }}
              onVisibleRangeChange={(range) => {
                if (!range?.from || !range?.to) {
                  setChartVisibleRange(null);
                  return;
                }
                setChartVisibleRange((prev) => {
                  if (prev?.from === range.from && prev.to === range.to) {
                    return prev;
                  }
                  return range;
                });
              }}
              onCrosshairBarChange={(bar) => {
                setHoverCandle(
                  bar
                    ? {
                        open: bar.open,
                        high: bar.high,
                        low: bar.low,
                        close: bar.close,
                      }
                    : null,
                );
              }}
            />
          </div>
        </main>

        <section className="flex min-h-0 min-w-0 flex-col bg-[#101318] max-[1180px]:col-start-2 max-[1180px]:row-start-2">
          <div className="border-b border-[var(--border)] px-3 py-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-200">威科夫摘要</div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <MetricCard label="当前阶段" value={summary.phase_name} />
              <MetricCard label="箱体区间" value={currentPhaseRangeText} />
              <MetricCard label="阶段强度" value={`${formatNumber(summary.strength)}%`} />
              <MetricCard label="置信度" value={`${formatNumber(summary.confidence)}%`} />
              <MetricCard label="当前信号" value={pnfTarget.signal} />
              <MetricCard label="预测目标" value={pnfTarget.targetText} />
            </div>
            <div className="mt-2 text-[11px] text-[var(--muted)]">
              最近事件 {latestEvent ? `${latestEvent.label} · ${formatTradeDate(latestEvent.time)}` : "--"} · 交易日{" "}
              {formatTradeDate(tradeDateValue || latestBar?.time)}
            </div>
          </div>

          <section className="flex min-h-0 flex-1 flex-col px-3 py-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-200">点数图 (PnF)</div>
              <div className="text-[11px] text-[var(--muted)]">
                同范围 {formatTradeDate(pnfBars[0]?.time)} ~ {formatTradeDate(pnfBars[pnfBars.length - 1]?.time)}
              </div>
            </div>
            <div className="min-h-[260px] flex-1">
              <PointFigureCanvas columns={pnfColumns} boxSize={effectivePnfBoxSize} method={pnfMethod} className="h-full w-full rounded border border-[var(--border)] bg-[#0a0f16]" />
            </div>
            <div className="mt-2 text-xs text-[var(--muted)]">{pnfTarget.explain}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(Object.entries(pnfTarget.tiers) as Array<[PnfTargetMode, (typeof pnfTarget.tiers)[PnfTargetMode]]>).map(([key, tier]) => (
                <span
                  key={key}
                  className={`rounded border px-2 py-1 text-xs ${
                    key === pnfTarget.mode
                      ? "border-sky-400/40 bg-sky-500/15 text-sky-200"
                      : "border-[var(--border)] bg-[#121922] text-slate-300"
                  }`}
                >
                  {tier.label}: {tier.text}
                </span>
              ))}
            </div>
          </section>

          <div className="border-t border-[var(--border)] px-3 py-2">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-200">点数图参数</div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col gap-1 text-[11px] text-[var(--muted)]">
                价格来源
                <select
                  value={pnfMethod}
                  onChange={(event) => setPnfMethod(event.target.value === "close" ? "close" : "hilo")}
                  className="h-8 rounded border border-[var(--border)] bg-[#0a0f16] px-2 text-xs text-slate-100"
                >
                  <option value="hilo">高低价</option>
                  <option value="close">收盘价</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-[11px] text-[var(--muted)]">
                box 方式
                <select
                  value={pnfBoxMode}
                  onChange={(event) => setPnfBoxMode(parsePnfBoxMode(event.target.value))}
                  className="h-8 rounded border border-[var(--border)] bg-[#0a0f16] px-2 text-xs text-slate-100"
                >
                  <option value="atr">ATR(14)</option>
                  <option value="manual">手动输入</option>
                  <option value="percent">百分比(LTP)</option>
                </select>
              </label>
              {pnfBoxMode === "manual" ? (
                <label className="flex flex-col gap-1 text-[11px] text-[var(--muted)]">
                  手动 box
                  <input
                    type="number"
                    min={0.01}
                    step={0.01}
                    value={pnfBoxSize > 0 ? pnfBoxSize : atrBoxSize}
                    onChange={(event) => setPnfBoxSize(Math.max(0.01, Number(event.target.value) || atrBoxSize))}
                    className="h-8 rounded border border-[var(--border)] bg-[#0a0f16] px-2 text-xs text-slate-100"
                  />
                </label>
              ) : pnfBoxMode === "percent" ? (
                <label className="flex flex-col gap-1 text-[11px] text-[var(--muted)]">
                  百分比 (%)
                  <input
                    type="number"
                    min={0.1}
                    max={20}
                    step={0.1}
                    value={pnfPercent}
                    onChange={(event) => setPnfPercent(clampPnfPercent(Number(event.target.value) || 1))}
                    className="h-8 rounded border border-[var(--border)] bg-[#0a0f16] px-2 text-xs text-slate-100"
                  />
                </label>
              ) : (
                <MetricCard label="ATR box" value={formatNumber(atrBoxSize)} />
              )}
              <label className="flex flex-col gap-1 text-[11px] text-[var(--muted)]">
                反转数量
                <input
                  type="number"
                  min={1}
                  max={5}
                  step={1}
                  value={effectiveReversal}
                  onChange={(event) => setPnfReversal(clampPnfReversal(Number(event.target.value)))}
                  className="h-8 rounded border border-[var(--border)] bg-[#0a0f16] px-2 text-xs text-slate-100"
                />
              </label>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <MetricCard label="实际 box" value={formatNumber(effectivePnfBoxSize)} />
              <MetricCard label="列数" value={String(pnfColumns.length)} />
            </div>
          </div>
        </section>
      </div>

      <AddIntoWatchlistModal
        open={addIntoWatchlistOpen}
        symbol={search.symbol}
        displayName={displayName}
        watchlists={watchlistMeta}
        maxListsPerUser={maxListsPerUser}
        onClose={() => setAddIntoWatchlistOpen(false)}
        onAfterChange={async () => {
          await refreshWatchlistQueries();
        }}
      />
    </div>
  );
}

function normalizeBarsPayload(raw: unknown[]) {
  return raw
    .map((item) => {
      const typed = item as Record<string, unknown>;
      return {
        time: normalizeChartTime(typed.time || typed.trade_date || typed.date || ""),
        open: Number(typed.open || 0),
        high: Number(typed.high || 0),
        low: Number(typed.low || 0),
        close: Number(typed.close || 0),
        volume: Number(typed.volume || typed.vol || 0),
      };
    })
    .filter((item) => item.time);
}

function mergeBars(currentBars: ReturnType<typeof normalizeBarsPayload>, nextBars: ReturnType<typeof normalizeBarsPayload>) {
  const merged = new Map<string, (typeof currentBars)[number]>();
  [...currentBars, ...nextBars].forEach((bar) => {
    merged.set(bar.time, bar);
  });
  return Array.from(merged.values()).sort((left, right) => left.time.localeCompare(right.time));
}

function normalizeChartTime(raw: unknown) {
  const value = String(raw || "");
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  return value;
}

function toTradeDateDigits(raw: unknown) {
  return String(raw || "").replaceAll("-", "").slice(0, 8);
}

function formatTradeDate(value: unknown) {
  const raw = String(value || "");
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw || "--";
}

function toFiniteNumber(raw: unknown) {
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function normalizeLiveQuote(raw: unknown, fallbackTradeDate?: unknown): LiveQuoteSnapshot | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const typed = raw as Record<string, unknown>;
  const close = toFiniteNumber(typed.close);
  if (close == null) {
    return null;
  }
  let pctChg = toFiniteNumber(typed.pct_chg);
  let preClose = toFiniteNumber(typed.pre_close);
  let change = toFiniteNumber(typed.change);
  if (change == null && preClose != null) {
    change = close - preClose;
  }
  if (pctChg == null && preClose != null && preClose !== 0 && change != null) {
    pctChg = (change / preClose) * 100;
  }
  if (preClose == null && change != null) {
    preClose = close - change;
  }
  const tradeDateDigits = toTradeDateDigits(typed.trade_date || typed.time || fallbackTradeDate || "");
  return {
    close,
    pct_chg: pctChg,
    pre_close: preClose,
    change,
    trade_date: tradeDateDigits || null,
    vol: toFiniteNumber(typed.vol || typed.volume),
    amount: toFiniteNumber(typed.amount),
    source: typed.source == null ? null : String(typed.source || ""),
  };
}

function normalizeWatchlistSortField(raw: string) {
  const value = String(raw || "").trim().toLowerCase();
  if (!value) {
    return "pct_chg";
  }
  if (value === "display_name" || value === "name_cache") {
    return "name";
  }
  if (value === "volume") {
    return "vol";
  }
  if (value === "turnover") {
    return "amount";
  }
  return value;
}

function clampLimit(value: number) {
  return Math.max(120, Math.min(2000, Math.round(value)));
}

function measureSingleWatchlistWidth(rows: Array<{ display_name?: string | null; stock_name?: string | null; name_cache?: string | null; symbol: string }>) {
  if (typeof document === "undefined") {
    return 220;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return 220;
  }
  ctx.font = '12px "Noto Sans SC", sans-serif';
  const widest = rows.reduce((max, item) => {
    const name = item.display_name || item.stock_name || item.name_cache || item.symbol;
    return Math.max(max, Math.ceil(ctx.measureText(name).width));
  }, 0);
  const contentWidth = widest + 44;
  return Math.max(196, Math.min(460, contentWidth));
}

function normalizeMarkersFromRows(rows: WyckoffRow[]): { events: LocalWyckoffAnalysis["events"]; markers: WyckoffMarker[] } {
  const events: LocalWyckoffAnalysis["events"] = [];
  const markers: WyckoffMarker[] = [];
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row.spring) {
      events.push({ type: "spring", label: "SPRING", time: row.time, close: 0, phase: row.phase });
      markers.push({ time: row.time, position: "belowBar", color: "#0f9d7a", shape: "arrowUp", text: "SPR" });
    }
    if (row.sos) {
      events.push({ type: "sos", label: "SOS", time: row.time, close: 0, phase: row.phase });
      markers.push({ time: row.time, position: "belowBar", color: "#1f8ef1", shape: "circle", text: "SOS" });
    }
    if (row.upthrust) {
      events.push({ type: "upthrust", label: "UPTHRUST", time: row.time, close: 0, phase: row.phase });
      markers.push({ time: row.time, position: "aboveBar", color: "#dd5b42", shape: "arrowDown", text: "UT" });
    }
    if (row.sow) {
      events.push({ type: "sow", label: "SOW", time: row.time, close: 0, phase: row.phase });
      markers.push({ time: row.time, position: "aboveBar", color: "#b45309", shape: "circle", text: "SOW" });
    }
  }
  return { events, markers };
}

function normalizeWyckoffPayload(payload: WyckoffAnalysis, bars: ReturnType<typeof normalizeBarsPayload>): LocalWyckoffAnalysis {
  const rows = (payload.rows || []).map((row, index) => {
    const phaseRaw = String(row.phase || "neutral").toLowerCase();
    const phase = phaseRaw === "accumulation" || phaseRaw === "distribution" ? phaseRaw : "neutral";
    return {
      time: normalizeChartTime(row.time || bars[index]?.time || ""),
      phase,
      phase_name: String(row.phase_name || (phase === "accumulation" ? "吸筹" : phase === "distribution" ? "派发" : "中性")),
      strength: Number(row.strength || 0),
      confidence: Number(row.confidence || 0),
      pos: row.pos ?? null,
      atr: row.atr ?? null,
      tr_high: row.tr_high ?? null,
      tr_low: row.tr_low ?? null,
      acc_score: Number(row.acc_score || 0),
      dist_score: Number(row.dist_score || 0),
      spring: Boolean(row.spring),
      upthrust: Boolean(row.upthrust),
      sos: Boolean(row.sos),
      sow: Boolean(row.sow),
    } as WyckoffRow;
  });
  const markerBundle = normalizeMarkersFromRows(rows);
  const events = markerBundle.events.map((event) => {
    const row = rows.find((item) => item.time === event.time);
    const bar = bars.find((item) => item.time === event.time);
    return {
      ...event,
      close: bar?.close ?? 0,
      phase: row?.phase || event.phase,
    };
  });
  return {
    rows,
    segments: (payload.segments || []).map((segment) => ({
      phase: segment.phase === "accumulation" || segment.phase === "distribution" ? segment.phase : "neutral",
      start_time: normalizeChartTime(segment.start_time),
      end_time: normalizeChartTime(segment.end_time),
      start_index: Number(segment.start_index || 0),
      end_index: Number(segment.end_index || 0),
      top: Number(segment.top || 0),
      bottom: Number(segment.bottom || 0),
      confidence: Number(segment.confidence || 0),
      strength: Number(segment.strength || 0),
      bars: Number(segment.bars || 0),
    })),
    summary: {
      phase:
        payload.summary?.phase === "accumulation" || payload.summary?.phase === "distribution"
          ? payload.summary.phase
          : "neutral",
      phase_name: String(payload.summary?.phase_name || "中性"),
      strength: Number(payload.summary?.strength || 0),
      confidence: Number(payload.summary?.confidence || 0),
      pos: payload.summary?.pos ?? null,
      atr_pct: payload.summary?.atr_pct ?? null,
      event_counts: {
        spring: Number(payload.summary?.event_counts?.spring || 0),
        upthrust: Number(payload.summary?.event_counts?.upthrust || 0),
        sos: Number(payload.summary?.event_counts?.sos || 0),
        sow: Number(payload.summary?.event_counts?.sow || 0),
      },
    },
    events,
    markers: markerBundle.markers,
  };
}

function readWyckoffSessionFromLocal() {
  try {
    const raw = window.localStorage.getItem(WYCKOFF_SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return parseWyckoffSession(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeWyckoffSessionToLocal(session: WyckoffSessionState) {
  try {
    window.localStorage.setItem(WYCKOFF_SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    return;
  }
}

function parseWyckoffSession(raw: unknown): WyckoffSessionState | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const candidate = raw as Record<string, unknown>;
  const parsedBoxSize = Math.max(0, Number(candidate.pnfBoxSize) || 0);
  const hasExplicitBoxMode = typeof candidate.pnfBoxMode === "string";
  return {
    interval: parseInterval(candidate.interval),
    adjust: parseAdjust(candidate.adjust),
    watchlistView: parseWatchlistView(candidate.watchlistView),
    chartSubPane: parseWyckoffSubPane(candidate.chartSubPane),
    chartMainPaneRatio: clampRatio(Number(candidate.chartMainPaneRatio) || 0.76, 0.6, 0.86),
    limit: clampLimit(Number(candidate.limit) || 420),
    pnfBoxMode: hasExplicitBoxMode ? parsePnfBoxMode(candidate.pnfBoxMode) : parsedBoxSize > 0 ? "manual" : "atr",
    pnfBoxSize: parsedBoxSize,
    pnfPercent: clampPnfPercent(Number(candidate.pnfPercent) || 1),
    pnfReversal: clampPnfReversal(Number(candidate.pnfReversal) || 3),
    pnfMethod: candidate.pnfMethod === "close" ? "close" : "hilo",
  };
}

function parseInterval(raw: unknown): (typeof INTERVAL_OPTIONS)[number] {
  const value = String(raw || "D") as (typeof INTERVAL_OPTIONS)[number];
  return INTERVAL_OPTIONS.includes(value) ? value : "D";
}

function parseAdjust(raw: unknown): (typeof ADJUST_OPTIONS)[number] {
  const value = String(raw || "qfq") as (typeof ADJUST_OPTIONS)[number];
  return ADJUST_OPTIONS.includes(value) ? value : "qfq";
}

function formatAdjustLabel(value: (typeof ADJUST_OPTIONS)[number]) {
  if (value === "none") {
    return "不复权";
  }
  if (value === "hfq") {
    return "后复权";
  }
  return "前复权";
}

function parseWyckoffSubPane(raw: unknown): "vol" | "atr" | "rsi" {
  const value = String(raw || "vol").toLowerCase();
  if (value === "atr" || value === "rsi") {
    return value;
  }
  return "vol";
}

function parseWatchlistView(raw: unknown): "single" | "compact" {
  const value = String(raw || "").toLowerCase();
  if (value === "compact") {
    return value;
  }
  return "single";
}

function parsePnfBoxMode(raw: unknown): PnfBoxMode {
  const value = String(raw || "").toLowerCase();
  if (value === "manual" || value === "percent") {
    return value;
  }
  return "atr";
}

function clampPnfPercent(value: number) {
  if (!Number.isFinite(value)) {
    return 1;
  }
  return Math.max(0.1, Math.min(20, Number(value)));
}

function clampRatio(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.max(min, Math.min(max, value));
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-[var(--border)] bg-[#121922] px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-[var(--muted)]">{label}</div>
      <div className="mt-1 text-xs text-slate-200">{value}</div>
    </div>
  );
}

function UserAvatar({ avatarUrl, displayName }: { avatarUrl?: string | null; displayName: string }) {
  const initials = displayName.trim().slice(0, 2).toUpperCase() || "U";
  if (avatarUrl) {
    return <img src={avatarUrl} alt={displayName} className="size-7 rounded-full object-cover ring-1 ring-white/10" />;
  }
  return <div className="flex size-7 items-center justify-center rounded-full bg-[#1b2430] text-[11px] font-semibold text-slate-100 ring-1 ring-white/10">{initials}</div>;
}
